import React from 'react';
import { Shield, Award, Users, Target, Heart, Lightbulb } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import CTASection from '../components/CTASection';
import PageMeta from '../components/PageMeta';
import { Toaster } from '../components/ui/toaster';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/pagination';

const AboutUs = () => {
  const values = [
    {
      icon: Shield,
      title: 'Integrity',
      description: 'We operate with the highest ethical standards, ensuring transparency in all our services.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'We strive for perfection in every trademark application and client interaction.'
    },
    {
      icon: Heart,
      title: 'Client-Focused',
      description: 'Your success is our success. We tailor our services to meet your unique needs.'
    },
    {
      icon: Lightbulb,
      title: 'Innovation',
      description: 'We leverage the latest technology to streamline the trademark process.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      <PageMeta
        title="About Us - America's Trusted Trademark Experts | Trademark Vector"
        description="15+ years protecting American brands. Meet the Trademark Vector team behind 10,000+ successful registrations and 98% client satisfaction."
      />
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-[#C0392B] via-[#A93226] to-[#C0392B] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-white/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h1 className="text-5xl md:text-6xl font-bold text-white">
              About <span className="text-white/90 underline decoration-white/50">Trademark Vector</span>
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              We are a leading trademark and intellectual property services firm dedicated to 
              protecting businesses across the United States. With over 15 years of experience, 
              we've helped more than 10,000 businesses secure their brand identities.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-gradient-to-br from-[#FDFEFE] via-white to-[#FEF5F5] relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-72 h-72 bg-[#C0392B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-72 h-72 bg-[#7F8C8D]/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <Card className="border-2 border-[#7F8C8D]/20 hover:shadow-xl transition-all duration-300">
            <CardContent className="p-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
                {/* Left: Image */}
                <div className="relative">
                  <img
                    src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?w=800&q=80"
                    alt="Our Mission and Vision"
                    className="w-full h-full object-cover rounded-2xl shadow-lg min-h-[400px]"
                  />
                </div>
                
                {/* Right: Content */}
                <div className="space-y-8">
                  {/* Our Vision */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#7F8C8D]/10 rounded-xl flex items-center justify-center">
                        <Users className="w-6 h-6 text-[#7F8C8D]" />
                      </div>
                      <h2 className="text-3xl font-bold text-[#2C3E50]">Our Vision</h2>
                    </div>
                    <p className="text-[#2C3E50] leading-relaxed text-lg">
                      To become the most trusted name in trademark and intellectual property services 
                      nationwide. We envision a future where every entrepreneur and business owner has 
                      the knowledge and resources to protect their brand from day one, fostering innovation 
                      and entrepreneurship across America.
                    </p>
                  </div>

                  {/* Our Mission */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#C0392B]/10 rounded-xl flex items-center justify-center">
                        <Target className="w-6 h-6 text-[#C0392B]" />
                      </div>
                      <h2 className="text-3xl font-bold text-[#2C3E50]">Our Mission</h2>
                    </div>
                    <p className="text-[#2C3E50] leading-relaxed text-lg">
                      To empower businesses of all sizes with affordable, accessible, and expert trademark 
                      and intellectual property services. We believe every business deserves professional 
                      legal protection, and we're committed to making that a reality through transparent 
                      pricing and exceptional service.
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-gradient-to-br from-[#FEF5F5] via-white to-[#FEF5F5] relative overflow-hidden">
        {/* Decorative pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'linear-gradient(45deg, #C0392B 25%, transparent 25%), linear-gradient(-45deg, #C0392B 25%, transparent 25%)', backgroundSize: '60px 60px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50] text-center mb-12">
            Our Story
          </h2>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Content */}
            <div className="space-y-6 text-lg text-[#2C3E50] leading-relaxed">
              <p>
                Founded in 2010, Trademark Vector was born from a simple observation: trademark registration 
                was too complicated, too expensive, and too intimidating for most small business owners. 
                Our founders, a team of experienced intellectual property attorneys, set out to change that.
              </p>
              <p>
                We started with a mission to demystify the trademark process and make professional legal 
                services accessible to businesses of all sizes. What began as a small legal practice has 
                grown into one of the nation's most trusted trademark service providers, helping thousands 
                of businesses protect their brands every year.
              </p>
              <p>
                Today, we combine legal expertise with modern technology to deliver fast, affordable, and 
                reliable trademark services. Our team of experienced attorneys and trademark specialists 
                work tirelessly to ensure every application meets the highest standards of quality and 
                compliance.
              </p>
            </div>

            {/* Right: Image */}
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=800&q=80"
                alt="Our Team"
                className="w-full h-auto rounded-2xl shadow-2xl"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#C0392B]/20 to-transparent rounded-2xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 bg-gradient-to-b from-white to-[#FDFEFE] relative overflow-hidden">
        {/* Decorative grid */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{backgroundImage: 'radial-gradient(circle, #7F8C8D 1px, transparent 1px)', backgroundSize: '30px 30px'}} />
        </div>
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
              Our Core Values
            </h2>
            <p className="text-xl text-[#2C3E50] max-w-3xl mx-auto">
              These principles guide everything we do and ensure we deliver exceptional service
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => {
              const Icon = value.icon;
              return (
                <Card 
                  key={index}
                  className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group"
                >
                  <CardContent className="p-6">
                    <div className="flex flex-col items-center text-center space-y-4">
                      <div className="w-16 h-16 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 icon-float">
                        <Icon className="w-8 h-8 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                      </div>
                      <h3 className="text-xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">{value.title}</h3>
                      <p className="text-[#2C3E50] transition-colors duration-300">{value.description}</p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <SwiperSlide key={index}>
                    <Card className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group">
                      <CardContent className="p-6">
                        <div className="flex flex-col items-center text-center space-y-4">
                          <div className="w-16 h-16 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center group-hover:scale-110 group-hover:rotate-12 transition-all duration-500 icon-float">
                            <Icon className="w-8 h-8 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                          </div>
                          <h3 className="text-xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">{value.title}</h3>
                          <p className="text-[#2C3E50] transition-colors duration-300">{value.description}</p>
                        </div>
                      </CardContent>
                    </Card>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      {/* Why Clients Trust Us Section */}
      <section className="py-20 bg-gradient-to-br from-[#FDFEFE] to-white relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-[#C0392B]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-[#7F8C8D]/5 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 max-w-[1320px] relative z-10">
          <div className="text-center mb-16 space-y-4">
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C3E50]">
              Why Clients Trust Us
            </h2>
            <p className="text-xl text-[#2C3E50] max-w-3xl mx-auto">
              Our track record speaks for itself. Here's what sets us apart in the trademark industry.
            </p>
          </div>

          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
            {[
              {
                number: '15+',
                label: 'Years of Experience',
                description: 'Serving businesses across the USA'
              },
              {
                number: '10,000+',
                label: 'Successful Registrations',
                description: 'Trademarks protected and secured'
              },
              {
                number: '98%',
                label: 'Success Rate',
                description: 'Applications approved by USPTO'
              },
              {
                number: '24/7',
                label: 'Support Available',
                description: 'Expert assistance when you need it'
              }
            ].map((stat, index) => (
              <Card 
                key={index}
                className="border-2 border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group text-center"
              >
                <CardContent className="p-8">
                  <div className="space-y-4">
                    <div className="text-5xl font-bold text-[#C0392B] group-hover:scale-110 transition-transform duration-300">
                      {stat.number}
                    </div>
                    <h3 className="text-xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">
                      {stat.label}
                    </h3>
                    <p className="text-[#2C3E50] transition-colors duration-300">
                      {stat.description}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden mb-16">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {[
                {
                  number: '15+',
                  label: 'Years of Experience',
                  description: 'Serving businesses across the USA'
                },
                {
                  number: '10,000+',
                  label: 'Successful Registrations',
                  description: 'Trademarks protected and secured'
                },
                {
                  number: '98%',
                  label: 'Success Rate',
                  description: 'Applications approved by USPTO'
                },
                {
                  number: '24/7',
                  label: 'Support Available',
                  description: 'Expert assistance when you need it'
                }
              ].map((stat, index) => (
                <SwiperSlide key={index}>
                  <Card className="border-2 border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-2xl card-lift group text-center">
                    <CardContent className="p-8">
                      <div className="space-y-4">
                        <div className="text-5xl font-bold text-[#C0392B] group-hover:scale-110 transition-transform duration-300">
                          {stat.number}
                        </div>
                        <h3 className="text-xl font-bold text-[#2C3E50] group-hover:text-[#C0392B] transition-colors duration-300">
                          {stat.label}
                        </h3>
                        <p className="text-[#2C3E50] transition-colors duration-300">
                          {stat.description}
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          {/* Additional Trust Factors */}
          {/* Desktop View - Grid */}
          <div className="hidden md:grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Shield,
                title: 'Licensed Attorneys',
                description: 'All applications reviewed by U.S. registered trademark attorneys with years of experience in intellectual property law.'
              },
              {
                icon: Award,
                title: 'Industry Recognition',
                description: 'Award-winning service recognized by legal associations and business organizations nationwide.'
              },
              {
                icon: Users,
                title: 'Client Satisfaction',
                description: '4.9/5 average rating from thousands of satisfied clients who trust us to protect their brands.'
              }
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <Card 
                  key={index}
                  className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-xl card-lift group"
                >
                  <CardContent className="p-8 text-center">
                    <div className="w-20 h-20 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                      <Icon className="w-10 h-10 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                    </div>
                    <h3 className="text-xl font-bold text-[#2C3E50] mb-4 group-hover:text-[#C0392B] transition-colors duration-300">
                      {item.title}
                    </h3>
                    <p className="text-[#2C3E50] leading-relaxed transition-colors duration-300">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Mobile View - Slider */}
          <div className="md:hidden max-w-6xl mx-auto">
            <Swiper
              modules={[Pagination, Autoplay]}
              spaceBetween={20}
              slidesPerView={1}
              pagination={{ clickable: true }}
              autoplay={{ delay: 3000, disableOnInteraction: false }}
              className="pb-12"
            >
              {[
                {
                  icon: Shield,
                  title: 'Licensed Attorneys',
                  description: 'All applications reviewed by U.S. registered trademark attorneys with years of experience in intellectual property law.'
                },
                {
                  icon: Award,
                  title: 'Industry Recognition',
                  description: 'Award-winning service recognized by legal associations and business organizations nationwide.'
                },
                {
                  icon: Users,
                  title: 'Client Satisfaction',
                  description: '4.9/5 average rating from thousands of satisfied clients who trust us to protect their brands.'
                }
              ].map((item, index) => {
                const Icon = item.icon;
                return (
                  <SwiperSlide key={index}>
                    <Card className="border-[#7F8C8D]/20 hover:border-[#C0392B]/50 transition-all duration-500 hover:shadow-xl card-lift group">
                      <CardContent className="p-8 text-center">
                        <div className="w-20 h-20 bg-gradient-to-br from-[#C0392B]/10 to-[#C0392B]/5 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500">
                          <Icon className="w-10 h-10 text-[#C0392B] group-hover:scale-125 transition-transform duration-500" />
                        </div>
                        <h3 className="text-xl font-bold text-[#2C3E50] mb-4 group-hover:text-[#C0392B] transition-colors duration-300">
                          {item.title}
                        </h3>
                        <p className="text-[#2C3E50] leading-relaxed transition-colors duration-300">
                          {item.description}
                        </p>
                      </CardContent>
                    </Card>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        </div>
      </section>

      <CTASection showOffer={true} />
      <Toaster />
    </div>
  );
};

export default AboutUs;